'use client'

import React, { useCallback, useEffect } from 'react'
import Image from 'next/image'
import { Box, Fade, Theme, Toolbar, styled, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useRouter } from 'next/navigation'
import NavMenuItem from './NavMenuItem'
import MenuButton from './MenuButton'
import routes from './routes'
import dynamic from 'next/dynamic'
import BuyDropdown from '@components/NavBar/BuyDropdown'

const ConnectButton = dynamic(() => import('@dewicats/connect-button'), {
  ssr: false,
})

const LogoBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),

  [theme.breakpoints.up('xs')]: {
    flex: 1,
  },
  [theme.breakpoints.up('lg')]: {
    flex: 'unset',
  },
}))

const NavMenuBox = styled(Box)(
  ({ theme, app_is_live }: { theme?: Theme; app_is_live: string }) => ({
    display: 'flex',
    gap: theme.spacing(4),
    flex: 1,
    justifyContent: 'center',

    placeContent: app_is_live === 'true' ? '' : 'flex-start',
    marginLeft: app_is_live === 'true' ? '' : theme.spacing(4),

    [theme.breakpoints.up('xs')]: {
      display: 'none',
    },
    [theme.breakpoints.up('lg')]: {
      display: 'flex',
    },
  }),
)

const NavDrawerMenuBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}))

const NavMenuDropdown = styled(Box)(({ theme }) => ({
  [theme.breakpoints.up('xs')]: {
    display: 'none',
  },
  [theme.breakpoints.up('lg')]: {
    display: 'flex',
  },
}))

const NavIconButton = styled(MenuButton)(({ theme }) => ({
  [theme.breakpoints.up('xs')]: {
    display: 'flex',
  },
  [theme.breakpoints.up('lg')]: {
    display: 'none',
  },
}))

const connectPill = {
  // The bar's one primary action, in the landing site's pill: white fill,
  // black label, fully rounded, nothing drawn around it.
  borderRadius: 'var(--dewi_radius_rounded)',
  border: 'none',
  boxShadow: 'none',
  backgroundColor: 'var(--dewi_colors_gray_0)',
  color: 'var(--dewi_colors_dark_9)',
  fontWeight: 700,
  '&:hover': {
    backgroundColor: 'var(--dewi_colors_gray_2)',
    border: 'none',
  },
}

const NavConnectButton = styled(ConnectButton)(({ theme }) => ({
  ...connectPill,
  [theme.breakpoints.up('xs')]: {
    display: 'none',
  },
  [theme.breakpoints.up('lg')]: {
    display: 'flex',
  },
}))

type MainNavBarContentProps = {
  onToggleDrawer: () => void
  drawerOpen: boolean
}

const MainNavBarContent = ({
  onToggleDrawer,
  drawerOpen,
}: MainNavBarContentProps) => {
  const { t } = useTranslation()
  const router = useRouter()
  const theme = useTheme()

  const goHome = useCallback(() => {
    router.push('/')
  }, [router])

  return (
    <>
      <Toolbar>
        <LogoBox>
          {/* One mark, in white: the pixel cat the landing site puts in the
              top-left of its menu. It replaces the round photo badge and the
              wordmark that used to sit beside it behind a divider. */}
          <Image
            style={{ cursor: 'pointer' }}
            onClick={goHome}
            alt="DeWiCats"
            src="/assets/dewicats-logo-mark.svg"
            width={76}
            height={42}
            priority
          />
        </LogoBox>
        <NavMenuBox
          app_is_live={(
            process.env.NEXT_PUBLIC_REACT_APP_IS_LIVE?.toString() === 'true'
          ).toString()}
        >
          {routes().map((item) => (
            <NavMenuItem key={item.title} {...item} />
          ))}
          <BuyDropdown />
        </NavMenuBox>
        <NavConnectButton disableMagicLink />
        <NavIconButton hasCloseIcon={drawerOpen} onClick={onToggleDrawer} />
      </Toolbar>

      <Fade in={drawerOpen} timeout={1000}>
        <NavDrawerMenuBox>
          {routes().map((item) => (
            <NavMenuItem callback={onToggleDrawer} key={item.title} {...item} />
          ))}
          <BuyDropdown />
          <ConnectButton
            disableMagicLink
            sx={{
              ...connectPill,
              marginTop: theme.spacing(2),
            }}
          />
        </NavDrawerMenuBox>
      </Fade>
    </>
  )
}

export default MainNavBarContent
